import {
  Badge,
  Button,
  Callout,
  Checkbox,
  Dialog,
  Icon,
  Input,
} from "@obsina/design-system";
import {
  CheckCircle2,
  FolderOpen,
  Info,
  Library,
  Search,
  Trash2,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  deselectSharePointDrive,
  getSharePointDriveSelections,
  getSharePointSiteDrives,
  isSharePointOwnerPrivateRequired,
  listSharePointSites,
  selectSharePointDrive,
} from "./api";
import type { VisibilityClass } from "./domain";
import { APP_LOCALE } from "./locale";
import type { AppMessages } from "./messages";
import type {
  SharePointDrive,
  SharePointDriveSelection,
  SharePointSite,
} from "./sharepoint";

type LoadState = "idle" | "loading" | "ready" | "error" | "visibility_required";

export interface SharePointLibraryDialogProps {
  readonly apiBase: string | null;
  readonly connectionId: string;
  readonly connectionVisibility: VisibilityClass | null;
  readonly privateRequestError: string | null;
  readonly privateRequestPending: boolean;
  readonly messages: AppMessages;
  readonly onOpenChange: (open: boolean) => void;
  readonly onRequestPrivate: () => void;
  readonly onSelectionChange: (count: number, reloadSummary?: boolean) => void;
  readonly open: boolean;
}

function sortedSelections(
  selections: readonly SharePointDriveSelection[],
): readonly SharePointDriveSelection[] {
  return [...selections].sort(
    (left, right) =>
      // Sorted in the reader's own alphabet. Without the locale, Turkish
      // letters collate by code point and the visible order is wrong: I sorts
      // away from ı and Ş lands after Z.
      left.siteName.localeCompare(right.siteName, APP_LOCALE) ||
      left.driveName.localeCompare(right.driveName, APP_LOCALE),
  );
}

/**
 * Explicit SharePoint configuration step. Authorization and resource choice are
 * deliberately separate: Sites.Read.All can be granted while zero libraries
 * are selected, and the dialog never treats that state as synchronized.
 */
export function SharePointLibraryDialog({
  apiBase,
  connectionId,
  connectionVisibility,
  privateRequestError,
  privateRequestPending,
  messages,
  onOpenChange,
  onRequestPrivate,
  onSelectionChange,
  open,
}: SharePointLibraryDialogProps) {
  const copy = messages.sharePointLibraries;
  const selectedHeadingId = useId();
  const searchHeadingId = useId();
  const siteResultsHeadingId = useId();
  const librariesHeadingId = useId();
  const [selections, setSelections] = useState<
    readonly SharePointDriveSelection[]
  >([]);
  const [selectionState, setSelectionState] = useState<LoadState>("idle");
  const [selectionRetry, setSelectionRetry] = useState(0);
  const [query, setQuery] = useState("");
  const [queryError, setQueryError] = useState<string | null>(null);
  const [sites, setSites] = useState<readonly SharePointSite[]>([]);
  const [siteState, setSiteState] = useState<LoadState>("idle");
  // The applied filter, separate from the input draft: it names what the list
  // currently shows, so an unfiltered list is never described as a search
  // result. `truncated` is the service's own page statement, never a total.
  const [appliedQuery, setAppliedQuery] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [selectedSite, setSelectedSite] = useState<SharePointSite | null>(null);
  const [drives, setDrives] = useState<readonly SharePointDrive[]>([]);
  const [driveState, setDriveState] = useState<LoadState>("idle");
  const [pendingDriveId, setPendingDriveId] = useState<string | null>(null);
  const [mutationFailure, setMutationFailure] = useState<
    "generic" | "visibility_required" | null
  >(null);
  const [announcement, setAnnouncement] = useState("");
  const siteSearchController = useRef<AbortController | null>(null);
  const driveController = useRef<AbortController | null>(null);

  useEffect(() => {
    if (open) return;
    siteSearchController.current?.abort();
    driveController.current?.abort();
    setQuery("");
    setQueryError(null);
    setSites([]);
    setSiteState("idle");
    setAppliedQuery(null);
    setTruncated(false);
    setSelectedSite(null);
    setDrives([]);
    setDriveState("idle");
    setMutationFailure(null);
    setAnnouncement("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (connectionVisibility === "tenant_shared") {
      setSelectionState("visibility_required");
      return;
    }
    if (!apiBase) {
      setSelectionState("error");
      return;
    }
    const controller = new AbortController();
    setSelectionState("loading");
    getSharePointDriveSelections(apiBase, connectionId, controller.signal)
      .then((value) => {
        if (controller.signal.aborted) return;
        setSelections(sortedSelections(value));
        setSelectionState("ready");
        onSelectionChange(value.length);
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) {
          setSelectionState(
            isSharePointOwnerPrivateRequired(reason)
              ? "visibility_required"
              : "error",
          );
        }
      });
    return () => controller.abort();
  }, [
    apiBase,
    connectionId,
    connectionVisibility,
    onSelectionChange,
    open,
    selectionRetry,
  ]);

  useEffect(
    () => () => {
      siteSearchController.current?.abort();
      driveController.current?.abort();
    },
    [],
  );

  const loadSites = useCallback(
    (filter: string | null) => {
      if (!apiBase) {
        setSiteState("error");
        return;
      }
      setQueryError(null);
      setSelectedSite(null);
      setDrives([]);
      setDriveState("idle");
      siteSearchController.current?.abort();
      const controller = new AbortController();
      siteSearchController.current = controller;
      setSiteState("loading");
      listSharePointSites(apiBase, connectionId, filter, controller.signal)
        .then((page) => {
          if (controller.signal.aborted) return;
          setSites(page.sites);
          setTruncated(page.truncated);
          setAppliedQuery(filter);
          setSiteState("ready");
        })
        .catch((reason: unknown) => {
          if (!controller.signal.aborted) {
            setSiteState(
              isSharePointOwnerPrivateRequired(reason)
                ? "visibility_required"
                : "error",
            );
          }
        });
    },
    [apiBase, connectionId],
  );

  // Opening the dialog lists the sites this account can reach. Searching is a
  // way to narrow that list, never the precondition for seeing one.
  useEffect(() => {
    if (!open || connectionVisibility === "tenant_shared") return;
    loadSites(null);
  }, [connectionVisibility, loadSites, open]);

  const runSiteSearch = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (selectionState === "visibility_required") return;
    const normalized = query.trim();
    if (normalized.length > 0 && normalized.length < 2) {
      setQueryError(copy.searchTooShort);
      return;
    }
    loadSites(normalized.length === 0 ? null : normalized);
  };

  const showAllSites = () => {
    setQuery("");
    loadSites(null);
  };

  const loadSiteDrives = (site: SharePointSite) => {
    setSelectedSite(site);
    setDrives([]);
    setMutationFailure(null);
    driveController.current?.abort();
    if (!apiBase) {
      setDriveState("error");
      return;
    }
    const controller = new AbortController();
    driveController.current = controller;
    setDriveState("loading");
    getSharePointSiteDrives(apiBase, connectionId, site.id, controller.signal)
      .then((value) => {
        if (controller.signal.aborted) return;
        setDrives(value);
        setDriveState("ready");
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) {
          setDriveState(
            isSharePointOwnerPrivateRequired(reason)
              ? "visibility_required"
              : "error",
          );
        }
      });
  };

  const removeSelection = async (selection: SharePointDriveSelection) => {
    if (!apiBase || pendingDriveId) return;
    setPendingDriveId(selection.driveId);
    setMutationFailure(null);
    try {
      await deselectSharePointDrive(apiBase, connectionId, selection.driveId);
      const next = selections.filter(
        (candidate) => candidate.driveId !== selection.driveId,
      );
      setSelections(next);
      setAnnouncement(copy.removedAnnouncement(selection.driveName));
      onSelectionChange(next.length, true);
    } catch (reason) {
      setMutationFailure(
        isSharePointOwnerPrivateRequired(reason)
          ? "visibility_required"
          : "generic",
      );
    } finally {
      setPendingDriveId(null);
    }
  };

  const toggleDrive = async (drive: SharePointDrive, selected: boolean) => {
    if (!apiBase || pendingDriveId || selectionState !== "ready") return;
    const existing = selections.find(
      (selection) => selection.driveId === drive.id,
    );
    setPendingDriveId(drive.id);
    setMutationFailure(null);
    try {
      let next: readonly SharePointDriveSelection[];
      if (selected) {
        if (existing) return;
        const created = await selectSharePointDrive(
          apiBase,
          connectionId,
          drive.id,
          drive.siteId,
        );
        next = sortedSelections([...selections, created]);
        setAnnouncement(copy.addedAnnouncement(created.driveName));
      } else {
        if (!existing) return;
        await deselectSharePointDrive(apiBase, connectionId, drive.id);
        next = selections.filter((selection) => selection.driveId !== drive.id);
        setAnnouncement(copy.removedAnnouncement(existing.driveName));
      }
      setSelections(next);
      onSelectionChange(next.length, true);
    } catch (reason) {
      setMutationFailure(
        isSharePointOwnerPrivateRequired(reason)
          ? "visibility_required"
          : "generic",
      );
    } finally {
      setPendingDriveId(null);
    }
  };

  const visibilityRequired =
    connectionVisibility === "tenant_shared" ||
    selectionState === "visibility_required" ||
    siteState === "visibility_required" ||
    driveState === "visibility_required" ||
    mutationFailure === "visibility_required";

  return (
    <Dialog
      description={copy.dialogDescription}
      footer={
        <Button onClick={() => onOpenChange(false)} variant="secondary">
          {copy.close}
        </Button>
      }
      onOpenChange={onOpenChange}
      open={open}
      size="lg"
      title={copy.dialogTitle}
    >
      <div className="sharepoint-library-manager">
        {open && (
          <p aria-live="polite" className="obsina-sr-only" role="status">
            {announcement}
          </p>
        )}

        {visibilityRequired && (
          <Callout title={copy.privateRequiredTitle} tone="warning">
            <p>{copy.privateRequiredBody}</p>
            <Button
              disabled={privateRequestPending}
              loading={privateRequestPending}
              loadingLabel={copy.makingPrivate}
              onClick={onRequestPrivate}
              size="sm"
            >
              {copy.makePrivate}
            </Button>
          </Callout>
        )}

        {visibilityRequired && privateRequestError && (
          <Callout
            announce
            title={messages.common.actionRequired}
            tone="danger"
          >
            {privateRequestError}
          </Callout>
        )}

        {mutationFailure === "generic" && (
          <Callout title={copy.mutationUnavailableTitle} tone="danger">
            {copy.mutationUnavailableBody}
          </Callout>
        )}

        <section
          aria-labelledby={selectedHeadingId}
          className="sharepoint-library-manager__section"
        >
          <div className="sharepoint-library-manager__section-head">
            <h3 id={selectedHeadingId}>{copy.selectedHeading}</h3>
            {selectionState === "ready" && selections.length > 0 && (
              <Badge tone="accent">
                {copy.cardSelectedCount(selections.length)}
              </Badge>
            )}
          </div>
          {selectionState === "visibility_required" ? null : selectionState ===
            "loading" ? (
            <p className="sharepoint-library-manager__state" role="status">
              {copy.selectedLoading}
            </p>
          ) : selectionState === "error" ? (
            <Callout title={copy.selectionsUnavailableTitle} tone="danger">
              <p>{copy.selectionsUnavailableBody}</p>
              <Button
                onClick={() => setSelectionRetry((value) => value + 1)}
                size="sm"
                variant="secondary"
              >
                {copy.retry}
              </Button>
            </Callout>
          ) : selectionState === "ready" && selections.length === 0 ? (
            <p className="sharepoint-library-manager__empty">
              {copy.selectedEmpty}
            </p>
          ) : (
            <ul className="sharepoint-selection-list">
              {selections.map((selection) => (
                <li key={selection.driveId}>
                  <span className="sharepoint-selection-list__icon">
                    <Icon icon={Library} size={17} />
                  </span>
                  <span className="sharepoint-selection-list__copy">
                    <strong>{selection.driveName}</strong>
                    <span>{selection.siteName}</span>
                  </span>
                  <Button
                    aria-label={copy.remove(selection.driveName)}
                    disabled={pendingDriveId !== null}
                    iconStart={<Icon icon={Trash2} size={14} />}
                    loading={pendingDriveId === selection.driveId}
                    onClick={() => void removeSelection(selection)}
                    size="sm"
                    variant="ghost"
                  >
                    {copy.remove(selection.driveName)}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Until the connection is private no site request can be made, so the
            browser is withheld rather than shown as a search that cannot run or
            a load that will never start. The callout above carries the reason
            and the action that unblocks it. */}
        {!visibilityRequired && (
          <>
            <section
              aria-labelledby={searchHeadingId}
              className="sharepoint-library-manager__section"
            >
              <h3 id={searchHeadingId}>{copy.searchHeading}</h3>
              <form
                className="sharepoint-site-search"
                noValidate
                onSubmit={runSiteSearch}
                role="search"
              >
                <Input
                  autoFocus
                  error={queryError}
                  hint={copy.searchHint}
                  label={copy.searchLabel}
                  maxLength={200}
                  minLength={2}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    if (queryError) setQueryError(null);
                  }}
                  placeholder={copy.searchPlaceholder}
                  startAdornment={<Icon icon={Search} size={16} />}
                  type="search"
                  value={query}
                />
                <div className="sharepoint-site-search__actions">
                  <Button
                    disabled={siteState === "loading"}
                    loading={siteState === "loading"}
                    loadingLabel={copy.searching}
                    type="submit"
                  >
                    {copy.search}
                  </Button>
                  {appliedQuery !== null && (
                    <Button
                      disabled={siteState === "loading"}
                      onClick={showAllSites}
                      type="button"
                      variant="ghost"
                    >
                      {copy.showAllSites}
                    </Button>
                  )}
                </div>
              </form>
            </section>

            <div className="sharepoint-library-browser">
              <section
                aria-labelledby={siteResultsHeadingId}
                className="sharepoint-library-browser__pane"
              >
                <div className="sharepoint-library-manager__section-head">
                  <h3 id={siteResultsHeadingId}>{copy.siteResultsHeading}</h3>
                  {siteState === "ready" && sites.length > 0 && (
                    <span className="sharepoint-library-manager__count">
                      {copy.siteResultCount(sites.length)}
                    </span>
                  )}
                </div>
                {siteState === "idle" || siteState === "loading" ? (
                  <p
                    className="sharepoint-library-manager__state"
                    role="status"
                  >
                    {copy.sitesLoading}
                  </p>
                ) : siteState === "error" ? (
                  <Callout title={copy.sitesUnavailableTitle} tone="danger">
                    <p>{copy.sitesUnavailableBody}</p>
                    <Button onClick={() => loadSites(appliedQuery)} size="sm">
                      {copy.retry}
                    </Button>
                  </Callout>
                ) : sites.length === 0 ? (
                  <p className="sharepoint-library-manager__empty">
                    {appliedQuery === null ? copy.sitesNone : copy.sitesEmpty}
                  </p>
                ) : (
                  <>
                    <ul className="sharepoint-site-list">
                      {sites.map((site) => (
                        <li key={site.id}>
                          <button
                            aria-pressed={selectedSite?.id === site.id}
                            onClick={() => loadSiteDrives(site)}
                            type="button"
                          >
                            <span className="sharepoint-site-list__icon">
                              <Icon icon={FolderOpen} size={17} />
                            </span>
                            <span>
                              <strong>{site.displayName}</strong>
                              <small>{site.name}</small>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                    {truncated && (
                      <p className="sharepoint-library-manager__truncated">
                        <Icon icon={Info} size={14} />
                        {copy.sitesTruncated(sites.length)}
                      </p>
                    )}
                  </>
                )}
              </section>

              <section
                aria-labelledby={librariesHeadingId}
                className="sharepoint-library-browser__pane"
              >
                <h3 id={librariesHeadingId}>
                  {selectedSite
                    ? copy.librariesFor(selectedSite.displayName)
                    : copy.libraryType}
                </h3>
                {!selectedSite ? (
                  <p className="sharepoint-library-manager__empty">
                    {copy.chooseSite}
                  </p>
                ) : driveState === "loading" ? (
                  <p
                    className="sharepoint-library-manager__state"
                    role="status"
                  >
                    {copy.librariesLoading}
                  </p>
                ) : driveState === "error" ? (
                  <Callout title={copy.librariesUnavailableTitle} tone="danger">
                    <p>{copy.librariesUnavailableBody}</p>
                    <Button
                      onClick={() => loadSiteDrives(selectedSite)}
                      size="sm"
                    >
                      {copy.retry}
                    </Button>
                  </Callout>
                ) : driveState === "ready" && drives.length === 0 ? (
                  <p className="sharepoint-library-manager__empty">
                    {copy.librariesEmpty}
                  </p>
                ) : (
                  <ul className="sharepoint-drive-list">
                    {drives.map((drive) => {
                      const checked = selections.some(
                        (selection) => selection.driveId === drive.id,
                      );
                      return (
                        <li data-selected={checked || undefined} key={drive.id}>
                          <Checkbox
                            aria-label={`${drive.name}, ${copy.libraryType}`}
                            checked={checked}
                            disabled={
                              selectionState !== "ready" ||
                              pendingDriveId !== null
                            }
                            label={
                              <span className="sharepoint-drive-list__copy">
                                <strong>{drive.name}</strong>
                                <span>{copy.libraryType}</span>
                              </span>
                            }
                            onChange={(event) =>
                              void toggleDrive(drive, event.target.checked)
                            }
                          />
                          {checked && (
                            <Badge tone="success">
                              <Icon icon={CheckCircle2} size={12} />
                              {copy.selected}
                            </Badge>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
